// Mensagem de falha de upload de arquivo (currículo, foto, áudio).
//
// Um upload que morre sem `error.response` não é "arquivo grande demais": a
// resposta não chegou ao browser. Isso acontece quando algo entre o candidato e
// a API recusa a requisição e responde sem header de CORS — o caso real é o
// firewall da AWS derrubar o POST porque os bytes do PDF batem com assinatura
// de SQL injection, e o browser, sem o CORS, só consegue reportar "erro de
// rede". O axios entrega `error.response === undefined` exatamente igual a uma
// queda de conexão.
//
// O código antigo respondia "o tamanho máximo do arquivo é de 15MB" para
// qualquer erro desses. Para um currículo de 200 KB isso é falso, e é a pior
// orientação possível: o candidato tenta de novo com o mesmo arquivo, que nunca
// vai passar. Aqui o limite só é citado quando o arquivo de fato passa dele.

export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

const REJECTED_IN_TRANSIT =
  "Não conseguimos enviar este arquivo. Tente novamente; se o erro repetir, " +
  "gere o arquivo de novo (salvar como PDF) ou envie em outro formato.";

const GENERIC =
  "Ocorreu um erro ao enviar o arquivo. Atualize a página e tente novamente!";

/**
 * Primeira mensagem de validação que a API devolveu, se houver.
 * O formato é `{ errors: [{ errors: 'texto' }] }`; o acesso direto a
 * `errors[0].errors` estourava quando a resposta não tinha esse array.
 */
function serverMessage(error) {
  const data = error && error.response && error.response.data;

  if (!data) {
    return null;
  }

  const first = Array.isArray(data.errors) ? data.errors[0] : null;

  return (
    (first && (first.errors || first.message)) ||
    (typeof first === "string" ? first : null) ||
    data.message ||
    null
  );
}

/**
 * @param {Error} error erro do axios
 * @param {File} [file] arquivo que o usuário escolheu, para conferir o tamanho
 * @param {number} [limitBytes]
 * @returns {string} texto para mostrar ao usuário
 */
export function uploadErrorMessage(error, file, limitBytes = MAX_UPLOAD_BYTES) {
  if (file && file.size > limitBytes) {
    const mb = (file.size / (1024 * 1024)).toFixed(1).replace(".", ",");
    const limitMb = Math.round(limitBytes / (1024 * 1024));

    return `Este arquivo tem ${mb} MB. O tamanho máximo é de ${limitMb} MB.`;
  }

  if (error && error.response) {
    return serverMessage(error) || GENERIC;
  }

  return REJECTED_IN_TRANSIT;
}

/**
 * `true` quando a resposta não chegou ao browser. Serve para o rastreio saber
 * separar "a API recusou" de "a requisição não chegou lá" — sem isso os dois
 * casos viram o mesmo evento e o problema fica invisível.
 */
export function isRejectedInTransit(error) {
  return Boolean(error) && !error.response;
}
